import React from 'react'
import { useParams } from 'react-router-dom'
import { LoadingSkeleton } from 'components/Datalist'
import { Box } from 'components/Layout'
import { useUser, UserPermissions, useAuth, useResendUserInvitation } from '@modmd/data'
import { ShowUserCode } from 'sharedComponents/ShowUserCode'
import Card from 'components/Card'
import DashboardPage from 'internal-portal/sharedComponents/DashboardPage'
import { Loading } from 'components/Loading'
import { COLOR } from 'theme'
import { lighten } from 'polished'
import Icon from 'components/Icon'
import Button from 'components/Button'
import { useToaster } from 'utils/useToaster'
import { ROUTES } from 'internal-portal/constants/routes'
import { UserDatalists } from './UserDatalists'
import { TabberProvider } from '../../../contexts/TabberProvider'

export interface Params {
  userId: string
}

export const UserDetails = () => {
  const { data: userAuth, hasPermission } = useAuth()
  const { userId } = useParams<Params>()
  const { setToastMessage } = useToaster()
  const { data: userData, loading: userLoading } = useUser({
    userId,
  })

  const [resendInvitation, { loading: resendIvitationLoading }] = useResendUserInvitation()

  const handleResendInvitation = async () => {
    try {
      if (userData?.user?.email)
        await resendInvitation({ variables: { email: userData?.user?.email } })
      setToastMessage(`Invitation has been re-sent`, 'success')
    } catch (error) {
      // Do nothing
    }
  }

  return (
    <TabberProvider>
      <DashboardPage
        Header={
          !userData?.user?.isCompliant ? (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              height="100%"
              p="1rem"
              bg={lighten(0.3, COLOR.warning)}
            >
              Pending invitation
              {(hasPermission([UserPermissions.RESEND_INVITATION]) ||
                (hasPermission([UserPermissions.RESEND_INVITATION_SPECIFIC_GROUP]) &&
                  userData?.user?.Groups.map(({ id }) => id).some((el) =>
                    userAuth.User.GroupsWithRole.map(({ id }) => id).includes(el)
                  ))) && (
                <Button onClick={handleResendInvitation} disabled={resendIvitationLoading}>
                  Resend invitation
                </Button>
              )}
            </Box>
          ) : null
        }
      >
        <Box flex="3" minWidth="280px" display="grid" gridGap="1rem" alignContent="start">
          <Card height="100%">
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              height="100%"
              p="1rem"
            >
              {userData?.user && (
                <Box display="flex" alignItems="center" height="100%" p="1rem">
                  {userData.user.parentId && (
                    <>
                      <Button
                        ml="1rem"
                        to={`/${ROUTES.USERS}/${Number(userData.user.parentId)}`}
                        appearance="link"
                      >
                        {`${userData?.user.ParentUser?.firstName || ''} ${
                          userData?.user.ParentUser?.lastName || ''
                        }`}
                      </Button>
                      <Icon.ArrowIosForwardOutline />
                    </>
                  )}
                  {`${userData?.user.firstName || ''} ${userData?.user.lastName || ''}`}
                </Box>
              )}
              <ShowUserCode isBarcode userId={userId} />
            </Box>
          </Card>
          {userLoading ? (
            <Card height="100%">
              <Loading isLoading skeletonComp={<LoadingSkeleton columns={3} rows={3} />} />
            </Card>
          ) : (
            userData?.user && (
              <Box height="100%">
                <UserDatalists userId={userId} />
              </Box>
            )
          )}
        </Box>
      </DashboardPage>
    </TabberProvider>
  )
}
